/** @format */

import { useState, useContext, useEffect, useRef, useMemo, useCallback } from "react";
import { Context } from "../stores/Provider";
import { GetMethod } from "../api/Api";
import { getSession } from "../sessionStorage/sessionStorageMethod";
import { useLocation } from "react-router-dom";
import { logoutAbnormalFunction } from "../function/logoutAbnormalFunction";
import { initApiModelType, commentResponseType } from "../constance/typeModel";
import { inquiryCommentDataType } from "../constance/providerTypeModel";
import { initApiResponse, inquirySlideInPaths, entryGuideInPaths } from "../constance/constance";

const useInquiryNavHooks = (isLogin: boolean | undefined, navigateByAgent: Function) => {
  const location = useLocation();
  const { state, dispatch } = useContext(Context);

  let accordionNum: number | null = null;
  if (location.state !== null) {
    accordionNum = location.state.accordionNum;
  }

  const loginInfoData = getSession("login");
  const userInfoData = getSession("user");
  let entry_id: null | number = null;
  if (userInfoData !== null) entry_id = userInfoData.entry_id;

  const agentPath = useMemo(() => state.agentPath, [state.agentPath]);
  const path = useMemo(() => location.pathname.replace(agentPath, ""), [location.pathname, agentPath]);
  const InquiryData = useMemo(() => state.inquiry.inquiry_all_data, [state.inquiry.inquiry_all_data]);
  const InquiryCommentData = useMemo(() => state.inquiry.inquiry_comment_data, [state.inquiry.inquiry_comment_data]);

  const toggleRef = useRef();
  const [toggle, setToggle] = useState(false);
  const toggleChangeHandler = useCallback(() => setToggle((prev) => !prev), []);

  const [naviFlag, setNaviFlag] = useState(false);
  const naviToggleChangeHandler = useCallback(() => setNaviFlag((prev) => !prev), []);

  const slideInShowFlag = inquirySlideInPaths.includes(path);
  const EntryGuideShowFlag = entryGuideInPaths.includes(path);

  // API接続で不備連携情報（お問い合わせ情報）の取得
  const getDataOfInquiryAll = useCallback(async () => {
    const reqData = { entry_id: entry_id };
    let response: initApiModelType<commentResponseType> = initApiResponse;
    response = await GetMethod(reqData, "/tickets");
    if (response.code === 200 && response.response_data !== undefined) {
      //お問い合わせ項目がある時
      dispatch({
        type: "set-inquiry-data",
        payload: response.response_data.tickets,
      });
    } else if (response.code === 204) {
      //お問い合わせ項目が無い時
      dispatch({
        type: "set-inquiry-data",
        payload: [],
      });
    } else if (response.code === 401) {
      return logoutAbnormalFunction(response, dispatch, navigateByAgent, state.agentPath, state.zAccount);
    } else {
      return navigateByAgent("/system_error");
    }
    return response;
  }, [dispatch, entry_id, navigateByAgent, state.agentPath, state.zAccount]);

  // API接続でコメント情報の取得
  const getDataOfInquiryCommentAll = useCallback(
    async (responseInquiry: {
      response_data: {
        tickets: {
          ticket_id: number;
        }[];
      };
    }) => {
      let commentsArray: inquiryCommentDataType[][] = [];
      const inquiryData = responseInquiry.response_data.tickets;
      await Promise.all(
        inquiryData.map(async (data: { ticket_id: number }) => {
          const reqData = {
            entry_id: entry_id,
            ticket_id: data.ticket_id,
          };
          const { code, response_data, message } = await GetMethod<typeof reqData, commentResponseType>(
            reqData,
            "/tickets/comments",
          );
          const response = {
            code: code,
            message: message,
          };
          if (code === 200 && response_data !== undefined) {
            commentsArray.push(response_data.comments);
          } else if (code === 401) {
            return logoutAbnormalFunction(response, dispatch, navigateByAgent, state.agentPath, state.zAccount);
          } else {
            return navigateByAgent("/system_error");
          }
        }),
      );
      commentsArray.sort((a: { created_at: string }[], b: { created_at: string }[]) => {
        if (a[0].created_at < b[0].created_at) {
          return 1;
        } else {
          return -1;
        }
      });

      dispatch({
        type: "set-inquiry-comment-data",
        payload: commentsArray,
      });
    },
    [dispatch, entry_id, navigateByAgent, state.agentPath, state.zAccount],
  );

  const getDataInquiry = useCallback(async () => {
    const response = await getDataOfInquiryAll();
    //不備が0個の場合リターン
    if (
      response === undefined ||
      response.response_data === undefined ||
      Object.values(response.response_data).length === 0
    )
      return;
    await getDataOfInquiryCommentAll(response);
  }, [getDataOfInquiryAll, getDataOfInquiryCommentAll]);

  //チケットIDで不備オブジェクト取得
  const serchInquiryData = useCallback(
    (ticketId: number) => {
      let returnObj = {
        status: "",
        title: "",
        ticket_id: 0,
      };
      InquiryData.forEach((inquiry: { status: string; title: string; ticket_id: number }) => {
        if (inquiry.ticket_id === ticketId) {
          returnObj = inquiry;
        }
      });
      return returnObj;
    },
    [InquiryData],
  );

  const nonClosedCount = useMemo(() => {
    let count = 0;
    InquiryData.forEach((data: { status: string }) => {
      if (data.status !== "CLOSED") count++;
    });
    return count;
  }, [InquiryData]);

  const inquiryCount = useMemo(() => {
    const countData = { closed: 0, active: 0 };
    InquiryData.forEach((data: { status: string }) => {
      if (data.status === "CLOSED") countData.closed += 1;
      if (data.status === "ACTIVE") countData.active += 1;
    });
    return countData;
  }, [InquiryData]);

  useEffect(() => {
    if (!isLogin || !entry_id || !loginInfoData || loginInfoData.code !== 200) return;
    getDataInquiry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    toggleChangeHandler,
    toggle,
    toggleRef,
    naviToggleChangeHandler,
    naviFlag,
    accordionNum,
    path,
    slideInShowFlag,
    EntryGuideShowFlag,
    InquiryData,
    InquiryCommentData,
    serchInquiryData,
    nonClosedCount,
    inquiryCount,
  };
};

export default useInquiryNavHooks;
